/**
 * 🎓 Phase 15.2 #6 — Contextual Smart Tutorial Triggers
 *
 * Watches for first-of-kind creator milestones and surfaces a smart
 * nudge: a toast + a one-click "Show me how" that opens the relevant
 * tutorial overlay.
 *
 * Triggers shipped:
 *   - first_upload   → "🎉 Track is live — share it now to kick momentum"
 *                       (opens upload_track tutorial step 4 onwards)
 *   - first_feature  → "🚀 You just got featured — here's how the Surge works"
 *                       (opens feature_spotlight tutorial)
 *   - first_live     → "🎙️ Your first Beam — invite your supporters!"
 *                       (opens live_beam tutorial step 4)
 *   - first_crew     → "🤝 First crew joined — explore the Community"
 *                       (no tutorial yet — shows informational toast)
 *
 * Storage: `localStorage` flags per-trigger so we never re-nudge the
 * same artist for the same milestone. The flags are per-device by
 * design; backend persistence is overkill for one-off nudges.
 *
 * Usage:
 *   import { fireTutorialTrigger } from "@/lib/tutorialTriggers";
 *   fireTutorialTrigger("first_upload", { trackId: sub.submission_id });
 *
 * Each call is idempotent — only the FIRST call of each trigger
 * produces a nudge. Future calls no-op silently.
 */
import { toast } from "sonner";

const NUDGES = {
  first_upload: {
    emoji: "🎉",
    title: "Your first track is live!",
    body: "Want to give it a boost? Share the link with your top 3 fans first — early plays + gifts push your momentum score way faster.",
    section: "upload_track",
    startAtStep: 4, // jump to the "Pro tip — share" step
    cta: "Show me how",
  },
  first_feature: {
    emoji: "🚀",
    title: "You just got featured!",
    body: "Your track is in the Surge cycle now — extra plays will drip in over the next 7 days. Tap to see exactly how it works.",
    section: "feature_spotlight",
    startAtStep: 0,
    cta: "How features work",
  },
  first_live: {
    emoji: "🎙️",
    title: "First Beam started!",
    body: "Drop the Invite button to ping your followers — most beams 2-3x their audience after one ping.",
    section: "live_beam",
    startAtStep: 3, // jump to gifts/invites step
    cta: "Open Beams tutorial",
  },
  first_crew: {
    emoji: "🤝",
    title: "Welcome to your first crew",
    body: "Crews are private circles for collabs, feedback, and listening parties. The full crew tutorial drops in Phase 15.2.",
    section: null,
    startAtStep: 0,
    cta: null,
  },
};

const FLAG_KEY = (kind) => `rgm.tutorial.trigger.${kind}.fired`;

const hasFired = (kind) => {
  try {
    return localStorage.getItem(FLAG_KEY(kind)) === "1";
  } catch {
    return false;
  }
};

const markFired = (kind) => {
  try {
    localStorage.setItem(FLAG_KEY(kind), "1");
  } catch { /* private mode, etc. */ }
};

/**
 * Fire a contextual tutorial nudge.
 * @param {keyof typeof NUDGES} kind
 * @param {{ openTutorial?: (section: string, opts?: object) => void }} opts
 *        Pass the `startSection` callback from useTutorial() if you
 *        want the CTA to launch the overlay. Otherwise CTA is hidden.
 */
export function fireTutorialTrigger(kind, opts = {}) {
  const nudge = NUDGES[kind];
  if (!nudge) return;
  if (hasFired(kind)) return;
  markFired(kind);

  const { openTutorial } = opts;
  const showCta = !!nudge.cta && !!nudge.section && typeof openTutorial === "function";

  toast.success(`${nudge.emoji} ${nudge.title}`, {
    description: nudge.body,
    duration: 9000,
    action: showCta
      ? {
          label: nudge.cta,
          onClick: () => {
            try {
              openTutorial(nudge.section, { source: "contextual_trigger" });
            } catch { /* swallow */ }
          },
        }
      : undefined,
  });
}

/** Test/admin helper — wipe local flags so triggers re-fire. */
export function resetTutorialTriggers() {
  try {
    Object.keys(NUDGES).forEach((k) => localStorage.removeItem(FLAG_KEY(k)));
  } catch { /* swallow */ }
}

/** Returns the list of triggers that have already fired locally. */
export function getFiredTriggers() {
  return Object.keys(NUDGES).filter(hasFired);
}
